import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend } from 'recharts';
import { BrainCircuit, Target, GitBranch, Activity, CheckCircle } from 'lucide-react';
// Mock Data
const accuracyHistory = Array.from({
  length: 30
}, (_, i) => ({
  day: `Day ${i + 1}`,
  xgboost: 92 + Math.random() * 4,
  lstm: 90 + Math.random() * 6,
  transformer: 94 + Math.random() * 3
}));
const modelMetrics = [{
  subject: 'RMSE',
  A: 120,
  B: 110,
  C: 90,
  fullMark: 150
}, {
  subject: 'MAE',
  A: 98,
  B: 130,
  C: 85,
  fullMark: 150
}, {
  subject: 'R²',
  A: 86,
  B: 130,
  C: 145,
  fullMark: 150
}, {
  subject: 'Bias',
  A: 99,
  B: 100,
  C: 120,
  fullMark: 150
}, {
  subject: 'Latency',
  A: 85,
  B: 90,
  C: 60,
  fullMark: 150
}];
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl p-3 shadow-xl z-50" data-id="element-2143">
        <p className="text-xs font-bold text-slate-900 mb-1" data-id="element-2144">{label}</p>
        {payload.map((entry: any) => <div key={entry.name} className="flex items-center gap-2 text-xs" data-id="element-2145">
            <div className="w-2 h-2 rounded-full" style={{
          backgroundColor: entry.color
        }} data-id="element-2146" />
            <span className="text-slate-600 capitalize" data-id="element-2147">{entry.name}:</span>
            <span className="font-mono font-medium" data-id="element-2148">
              {entry.value.toFixed(1)}%
            </span>
          </div>)}
      </div>;
  }
  return null;
};
export function MLModelPerformanceDashboard() {
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-indigo-50/30" data-id="element-2149">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2150">
        <div data-id="element-2151">
          <div className="flex items-center gap-2 mb-1" data-id="element-2152">
            <div className="p-2 bg-indigo-500 rounded-lg" data-id="element-2153">
              <BrainCircuit size={20} className="text-white" data-id="element-2154" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-2155">
              ML Model Performance
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-2156">
            Forecast accuracy, model drift, and feature importance
          </p>
        </div>

        <div className="flex items-center gap-2" data-id="element-2157">
          <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-bold" data-id="element-2158">
            v2.4.1 Active
          </span>
          <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold flex items-center gap-1" data-id="element-2159">
            <CheckCircle size={12} data-id="element-2160" /> Training Complete
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2161">
        {/* Accuracy Trend */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="lg:col-span-2 bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-2162">
          <div className="flex justify-between items-center mb-6" data-id="element-2163">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-2164">
              Forecast Accuracy (MAPE)
            </h3>
            <div className="flex gap-2" data-id="element-2165">
              <span className="text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded" data-id="element-2166">
                Transformer (Best)
              </span>
            </div>
          </div>

          <div className="h-[350px] w-full" data-id="element-2167">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2168">
              <LineChart data={accuracyHistory} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-2169">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-2170" />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} interval={5} data-id="element-2171" />
                <YAxis domain={[80, 100]} axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} data-id="element-2172" />
                <Tooltip content={<CustomTooltip data-id="element-2174" />} data-id="element-2173" />
                <Legend data-id="element-2175" />
                <Line type="monotone" dataKey="transformer" stroke="#4f46e5" strokeWidth={3} dot={false} data-id="element-2176" />
                <Line type="monotone" dataKey="xgboost" stroke="#0ea5e9" strokeWidth={2} dot={false} data-id="element-2177" />
                <Line type="monotone" dataKey="lstm" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" dot={false} data-id="element-2178" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Model Comparison Radar */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm flex flex-col items-center" data-id="element-2179">
          <h3 className="text-lg font-bold text-slate-900 mb-4 w-full text-left" data-id="element-2180">
            Model Metrics
          </h3>
          <div className="h-[300px] w-full" data-id="element-2181">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2182">
              <RadarChart cx="50%" cy="50%" outerRadius="80%" data={modelMetrics} data-id="element-2183">
                <PolarGrid stroke="#e2e8f0" data-id="element-2184" />
                <PolarAngleAxis dataKey="subject" tick={{
                fontSize: 12,
                fill: '#64748b'
              }} data-id="element-2185" />
                <PolarRadiusAxis angle={30} domain={[0, 150]} tick={false} axisLine={false} data-id="element-2186" />
                <Radar name="Transformer" dataKey="C" stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.3} data-id="element-2187" />
                <Radar name="XGBoost" dataKey="A" stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.3} data-id="element-2188" />
                <Legend data-id="element-2189" />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Feature Importance */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-2190">
        <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-2191">
          Top Feature Importance
        </h3>
        <div className="space-y-4" data-id="element-2192">
          {[{
          feature: 'Wind Forecast (DE)',
          importance: 0.85
        }, {
          feature: 'Gas Price (TTF)',
          importance: 0.72
        }, {
          feature: 'Temperature (FR)',
          importance: 0.65
        }, {
          feature: 'Nuclear Availability',
          importance: 0.58
        }].map((item, idx) => <div key={idx} data-id="element-2193">
              <div className="flex justify-between text-sm mb-1" data-id="element-2194">
                <span className="font-medium text-slate-700" data-id="element-2195">
                  {item.feature}
                </span>
                <span className="text-slate-500" data-id="element-2196">
                  {(item.importance * 100).toFixed(0)}%
                </span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-2197">
                <motion.div initial={{
              width: 0
            }} animate={{
              width: `${item.importance * 100}%`
            }} transition={{
              delay: 0.3 + idx * 0.1
            }} className="h-full bg-indigo-500 rounded-full" data-id="element-2198" />
              </div>
            </div>)}
        </div>
      </motion.div>
    </div>;
}